import type { RouteLocationNormalizedLoaded, Router } from 'vue-router';
import { JobApplicantStatus } from '@factoryfixinc/ats-interfaces';

import { getCurrentCandidateTab } from '@/composables/jobs/candidateTabs';
import { findJobApplicationStatusTitle } from '@/core/conversations/pro-profile/utils/application-statuses';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import type { AdvanceSelectedConversationResult } from '@/core/conversations/conversation-index/conversation-index.type';

export const FF_HINT_CONVERSATION_ID = '__ffHintConversationId';
export const FF_HINT_STATUS = '__ffHintStatus';

/**
 * After a candidate's application status is changed, advance the URL to the next candidate
 * in the filtered list (or to the project's empty state). When `movedConversationId` and
 * `newStatus` are both provided, also surface a `Moved to {Status}` snackbar with a
 * `View there` action that jumps back to the moved candidate (the deep-link handler
 * auto-applies the matching status filter on land). Bulk flows that already show their
 * own toast can omit those two fields to suppress the snackbar.
 */
export interface NavigateAfterStatusChangeArgs {
  router: Router;
  route: RouteLocationNormalizedLoaded;
  projectId: number;
  advanceResult: AdvanceSelectedConversationResult;
  movedConversationId?: number | null;
  newStatus?: JobApplicantStatus;
}

export async function navigateAfterConversationStatusChange({
  router,
  route,
  projectId,
  advanceResult,
  movedConversationId,
  newStatus,
}: NavigateAfterStatusChangeArgs): Promise<void> {
  const tab = getCurrentCandidateTab(route.path);

  if (advanceResult.selectionAdvanced) {
    const path = advanceResult.nextSelectedConversation
      ? `/jobs/${projectId}/candidates/${advanceResult.nextSelectedConversation.conversationId}/${tab}`
      : `/jobs/${projectId}/candidates`;
    await router.push({ path });
  }

  if (movedConversationId != null && newStatus) {
    SnackbarService.showSnackbar({
      variant: 'success',
      text: `Moved to ${findJobApplicationStatusTitle(newStatus)}`,
      showUndo: true,
      actionText: 'View there',
      undoAction: async () => {
        // Hint the correct status via history.state — the conversation index is
        // eventually-consistent and may still serve the OLD status on fetch.
        await router.push({
          path: `/jobs/${projectId}/candidates/${movedConversationId}/${tab}`,
          state: {
            [FF_HINT_CONVERSATION_ID]: movedConversationId,
            [FF_HINT_STATUS]: newStatus,
          },
        });
      },
    });
  }
}
